import optionsApi from "./optionsApi";

const createIdempotencyKey = () => {
  const values = new Uint32Array(4);
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(values);
    return Array.from(values, (value) => value.toString(16)).join("");
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export type OperationalControl = {
  id: string;
  status: string;
  reasonCode: string;
  effectiveAt: string;
  revision: string | number;
  policy: {
    incomingAccess?: "ALLOW" | "READ_ONLY" | "BLOCK";
    outboundCommunication?: "ALLOW" | "HOLD";
    automation?: "ALLOW" | "PAUSE";
    sessionAction?: "GRACEFUL_LOCK" | "REVOKE_NOW";
    employeeAccessMessage?: string;
    adminAccessMessage?: string;
  };
  targets: Array<{ targetType: string; targetId?: string }>;
};

export type OperationalControlTargets = {
  employees: Array<{ id: string; name: string; email: string; hrId: string }>;
  users: Array<{ id: number; full_name?: string; email_id?: string }>;
  roles: Array<{ id: string; name: string }>;
};

export const listOperationalControls = (companyId: string) =>
  optionsApi
    .get(`v1/operational-controls/${companyId}`)
    .then((response) => response.data.data as OperationalControl[]);

export const createOperationalControl = (companyId: string, payload: unknown) =>
  optionsApi.post(`v1/operational-controls/${companyId}`, payload, {
    headers: { "Idempotency-Key": createIdempotencyKey() },
  });

export const updateOperationalControl = (
  companyId: string,
  controlId: string,
  payload: unknown,
  revision: string | number
) =>
  optionsApi.put(`v1/operational-controls/${companyId}/${controlId}`, payload, {
    headers: {
      "Idempotency-Key": createIdempotencyKey(),
      "If-Match": String(revision),
    },
  });

export const listOperationalControlTargets = (companyId: string) =>
  optionsApi
    .get(`v1/operational-controls/${companyId}/targets`)
    .then((response) => response.data.data as OperationalControlTargets);

export const releaseOperationalControl = (
  companyId: string,
  controlId: string,
  revision: string | number
) =>
  optionsApi.post(
    `v1/operational-controls/${companyId}/${controlId}/release`,
    {},
    {
      headers: {
        "Idempotency-Key": createIdempotencyKey(),
        "If-Match": String(revision),
      },
    }
  );

export const listHeldDeliveries = (companyId: string) =>
  optionsApi
    .get(`v1/operational-controls/${companyId}/held-deliveries`)
    .then((response) => response.data.data as any[]);

export const resolveHeldDelivery = (
  companyId: string,
  deliveryId: string,
  action: "RELEASE" | "DISCARD" | "RETAIN",
  revision: string | number,
  reason?: string
) =>
  optionsApi.post(
    `v1/operational-controls/${companyId}/held-deliveries/${deliveryId}`,
    { action, reason },
    {
      headers: {
        "Idempotency-Key": createIdempotencyKey(),
        "If-Match": String(revision),
      },
    }
  );
